import { useCallback, useEffect, useState } from 'react';
import Cropper from 'react-easy-crop';

import { Alert, Button, Flex, Modal, Slider, Upload } from 'antd';

import { UploadOutlined } from '@ant-design/icons';
import classnames from 'classnames';
import { v4 as uuidv4 } from 'uuid';

import { getBase64 } from '../../helpers/common';

import messages from '../../constants/messages';
import { MAX_SIZE_MB } from '../../constants/user';

import { useModal } from '../../hooks/useModal';

import AppModal from '../../components/AppModal';

import { warnToastHandler } from '../action_notifier';
import { ALLOWED_IMG_TYPES, FILE_STATUS } from './constants';

function bytesToMB(bytes, decimals = 2) {
  if (!bytes) return 0;

  const MB = bytes / (1024 * 1024);

  return Number(MB.toFixed(decimals));
}

function isValidHttpUrl(string) {
  try {
    const url = new URL(string);
    return url.protocol === 'http:' || url.protocol === 'https:';
  } catch (err) {
    return false;
  }
}

function getFileNameFromUrl(url) {
  if (isValidHttpUrl(url)) {
    const pathname = new URL(url).pathname;
    return pathname.substring(pathname.lastIndexOf('/') + 1);
  } else {
    return 'current-image';
  }
}

const ImageUploader = ({ className, cropShape = 'rect', editMode = false, file = null, url = '', handleChange }) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [currentFile, setCurrentFile] = useState(null);
  const [fileList, setFileList] = useState([]);
  const [zoom, setZoom] = useState(1);

  const [cropModalVisible, openCropModal, closeCropModal] = useModal();

  useEffect(() => {
    if (file || url) {
      setFileList(url ? [{ status: FILE_STATUS.DONE, url, name: getFileNameFromUrl(url) }] : [file]);
    }
  }, []);

  const beforeUpload = file => {
    const { type, size } = file;
    const isAllowedType = ALLOWED_IMG_TYPES.includes(type);

    if (!isAllowedType) {
      warnToastHandler(messages.FILE_TYPE_NOT_ALLOWED);
      return Upload.LIST_IGNORE;
    }

    const fileSizeInMB = bytesToMB(size);
    const isLessThanAllowedSize = fileSizeInMB < MAX_SIZE_MB;

    if (!isLessThanAllowedSize) {
      warnToastHandler(messages.FILE_TOO_LARGE(`${MAX_SIZE_MB}MB`));
      return Upload.LIST_IGNORE;
    }

    setCurrentFile(file);
    setZoom(1);
    openCropModal();

    return false;
  };

  async function onRemove() {
    fetch(url)
      .then(res => res.blob())
      .then(blob => {
        const file = new File([blob], getFileNameFromUrl(url), { type: blob.type });

        setFileList([
          {
            uid: uuidv4(),
            name: file.name,
            status: FILE_STATUS.DONE,
            url
          }
        ]);

        getBase64(file).then(base64 => handleChange(base64));
      })
      .catch(err => console.error('Error loading original image:', err));

    return false;
  }

  const onCropComplete = useCallback((_, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const getCroppedImg = (imageSrc, cropPixels) => {
    const canvas = document.createElement('canvas');
    const image = new Image();
    image.src = imageSrc;

    return new Promise(resolve => {
      image.onload = () => {
        const ctx = canvas.getContext('2d');

        canvas.width = cropPixels.width;
        canvas.height = cropPixels.height;

        ctx.drawImage(
          image,
          cropPixels.x,
          cropPixels.y,
          cropPixels.width,
          cropPixels.height,
          0,
          0,
          cropPixels.width,
          cropPixels.height
        );

        canvas.toBlob(blob => {
          const croppedFile = new File([blob], currentFile.name, {
            type: currentFile.type
          });
          resolve(croppedFile);
        }, currentFile.type);
      };
    });
  };

  const handleCropSave = async () => {
    const reader = new FileReader();
    reader.readAsDataURL(currentFile);
    reader.onload = async () => {
      const croppedFile = await getCroppedImg(reader.result, croppedAreaPixels);
      setFileList([
        {
          uid: uuidv4(),
          name: croppedFile.name,
          status: FILE_STATUS.DONE,
          url: URL.createObjectURL(croppedFile)
        }
      ]);
      closeCropModal();
      const base64 = await getBase64(croppedFile);
      handleChange(base64);
    };
  };

  const onPreview = async file => {
    const src = file?.url ?? (await getBase64(file?.originFileObj));
    const image = new Image();
    image.src = src;
    const imgWindow = window.open(src);
    imgWindow?.document.write(image.outerHTML);
  };

  return (
    <>
      <Alert
        className="mt-2 mb-2"
        message={`Supported formats: JPG, JPEG, PNG. Maximum file size is ${MAX_SIZE_MB}MB.`}
        type="info"
        closable={true}
      />
      <Upload
        className={classnames(className, 'd-flex flex-column align-items-center w-full')}
        crossOrigin="anonymous"
        maxCount={1}
        fileList={fileList}
        disabled={!editMode}
        beforeUpload={beforeUpload}
        onPreview={onPreview}
        onRemove={onRemove}>
        <Button icon={<UploadOutlined />}>Click to Upload</Button>
      </Upload>

      <AppModal
        isOpen={cropModalVisible}
        title="Crop Image"
        confirmButtonText="Ok"
        handleCancel={closeCropModal}
        handleConfirm={handleCropSave}>
        <div style={{ position: 'relative', width: '100%', height: 400 }}>
          <Cropper
            aspect={1}
            crop={crop}
            cropShape={cropShape}
            image={currentFile ? URL.createObjectURL(currentFile) : null}
            zoom={zoom}
            onCropChange={setCrop}
            onCropComplete={onCropComplete}
            onZoomChange={setZoom}
          />
        </div>

        <div className="px-1 mt-4">
          <Slider min={1} max={3} step={0.1} value={zoom} onChange={val => setZoom(val)} />
        </div>
      </AppModal>
    </>
  );
};

export default ImageUploader;
